// Cache-while-revalidate store for customerInfo so the avatar / schema selector
// can paint instantly on app load while the API call refreshes in the background.
// Scoped by user email — never serve another user's data.
//
// We deliberately do NOT cache defaultDomain: it is per-schema, and a user can
// change selected schema between sessions. Hydrating it would briefly surface
// the wrong tenant context to filters/dashboards. Let the API response own it.

const KEY = 'qwa_customer_info_cache';

type CachedPayload = {
  email: string;
  customerInfo: any[];
};

export const getCachedCustomerInfo = (email: string | undefined | null): CachedPayload | null => {
  if (typeof window === 'undefined' || !email) return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPayload;
    if (parsed?.email !== email) return null;
    if (!Array.isArray(parsed.customerInfo) || parsed.customerInfo.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const setCachedCustomerInfo = (email: string | undefined | null, customerInfo: any[]): void => {
  if (typeof window === 'undefined' || !email) return;
  // Empty response means the user has no accounts (e.g. after access revoke).
  // Clear any stale non-empty cache rather than retain it.
  if (!Array.isArray(customerInfo) || customerInfo.length === 0) {
    clearCachedCustomerInfo();
    return;
  }
  try {
    const payload: CachedPayload = { email, customerInfo };
    window.localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // localStorage quota exceeded or disabled — fail silently
  }
};

export const clearCachedCustomerInfo = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
};
